import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Button, Card, CardBody, CardHeader, FormGroup, Input, Label, Table } from 'reactstrap';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { fetchDataAgnostic, postDataAgnostic, putData, constructUrl } from 'Services/dataApi';
import { formatBearerToken } from 'Common.functions';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const statusColorMap = {
  created: 'secondary',
  confirmed: 'secondary',
  sent_to_author: 'info',
  author_opened: 'info',
  in_progress: 'warning',
  uploaded: 'primary',
  submitted: 'success',
  supplement_required: 'warning',
  assigned_to_program: 'success',
  released_to_program: 'success',
  viewed_by_program: 'dark',
};

const AUTHOR_INVITE_TOKEN_STORAGE_KEY = 'lor_author_invite_token';

const getAuthorOtpConnectionId = () =>
  `${process.env.REACT_APP_KINDE_AUTHOR_OTP_CONNECTION_ID || process.env.REACT_APP_KINDE_EMAIL_OTP_CONNECTION_ID || ''}`.trim();

const buildKindeOtpLoginOptions = (invitePayload, inviteToken) => {
  const authUrlParams = {};
  const loginOptions = { app_state: { inviteToken } };
  const invitedEmail = `${invitePayload?.AuthorEmail || ''}`.trim();
  const connectionId = getAuthorOtpConnectionId();

  if (invitedEmail) {
    authUrlParams.login_hint = invitedEmail;
    loginOptions.loginHint = invitedEmail;
  }

  if (connectionId) {
    authUrlParams.connection_id = connectionId;
    loginOptions.connectionId = connectionId;
  }

  if (Object.keys(authUrlParams).length > 0) {
    loginOptions.authUrlParams = authUrlParams;
  }

  return loginOptions;
};

const parseOptions = (rawOptions) => {
  if (!rawOptions) {
    return [];
  }

  if (Array.isArray(rawOptions)) {
    return rawOptions;
  }

  try {
    const parsed = JSON.parse(rawOptions);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const LoRAuthorPortal = () => {
  const { token } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, isLoading, login, getToken } = useKindeAuth();

  const [invite, setInvite] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const [sessionToken, setSessionToken] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [answerMap, setAnswerMap] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isSavingFormResponses, setIsSavingFormResponses] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [isDraggingOverUploadZone, setIsDraggingOverUploadZone] = useState(false);
  const [signatureAttested, setSignatureAttested] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const autoLoginTokenRef = useRef(null);
  const fileInputRef = useRef(null);

  const questions = useMemo(() => requestDetails?.questions || [], [requestDetails]);
  const templateRequirements = useMemo(() => requestDetails?.templateRequirements || [], [requestDetails]);
  const requirementChecklist = requestDetails?.requirementChecklist || null;
  const needsSupplement = !!requestDetails?.needsSupplement;
  const statusEvents = useMemo(
    () =>
      (requestDetails?.statusEvents || []).filter(
        (event) => `${event?.Status || ''}`.toLowerCase() !== 'assigned_to_program',
      ),
    [requestDetails],
  );
  const submission = requestDetails?.submission || null;
  const uploadedDocumentUrl = requestDetails?.submissionDocumentUrl || '';
  const isLocked = submission?.Locked || !!submission?.SubmittedAt;

  const getAuthHeaders = async () => {
    const kindeToken = await getToken();
    return formatBearerToken(kindeToken);
  };

  const loadInvite = async () => {
    setIsLoadingData(true);
    return fetchDataAgnostic(`lor-author/invite/${token}`, null, null)
      .then((response) => {
        const invitePayload = response?.data?.invite || null;
        setInvite(invitePayload);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.message || 'Invite is invalid or expired.');
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  };

  const exchangeSession = async () => {
    const headers = await getAuthHeaders();
    return postDataAgnostic(
      'lor-author/session/exchange',
      null,
      {
        token,
      },
      headers,
    )
      .then((response) => {
        const nextSessionToken = response?.data?.authorSession?.sessionToken;
        if (!nextSessionToken) {
          throw new Error('Missing session token');
        }
        setSessionToken(nextSessionToken);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.message || 'Failed to authorize author session.');
      });
  };

  const loadAuthorRequest = async (showLoadingOverlay = true) => {
    if (!invite || !sessionToken) {
      return;
    }

    if (showLoadingOverlay) {
      setIsLoadingData(true);
    }
    const headers = await getAuthHeaders();
    headers['x-lor-session'] = sessionToken;
    return fetchDataAgnostic(
      'lor-author/request',
      {
        pk_LoRRequest: invite.pk_LoRRequest,
        sessionToken,
        fk_Template: selectedTemplateId || undefined,
      },
      headers,
    )
      .then((response) => {
        const details = response.data;
        setRequestDetails(details);
        const nextTemplateId = details?.selectedTemplateId || null;
        setSelectedTemplateId(nextTemplateId);

        const nextAnswerMap = {};
        (details?.answers || []).forEach((answer) => {
          const questionId = answer.fk_LoRTemplateQuestion;
          if (!questionId) {
            return;
          }

          if (answer.ValueNumber !== null && answer.ValueNumber !== undefined) {
            nextAnswerMap[questionId] = { value: answer.ValueNumber, type: 'number' };
          } else if (answer.ValueBool !== null && answer.ValueBool !== undefined) {
            nextAnswerMap[questionId] = { value: !!answer.ValueBool, type: 'boolean' };
          } else if (answer.ValueText !== null && answer.ValueText !== undefined) {
            nextAnswerMap[questionId] = { value: answer.ValueText, type: 'text' };
          } else if (answer.ValueJson !== null && answer.ValueJson !== undefined) {
            nextAnswerMap[questionId] = { value: answer.ValueJson, type: 'json' };
          }
        });
        setAnswerMap(nextAnswerMap);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.message || 'Failed to load request details.');
      })
      .finally(() => {
        if (showLoadingOverlay) {
          setIsLoadingData(false);
        }
      });
  };

  useEffect(() => {
    if (!token) {
      return;
    }

    sessionStorage.setItem(AUTHOR_INVITE_TOKEN_STORAGE_KEY, token);
    setInvite(null);
    setRequestDetails(null);
    setSessionToken('');
    setErrorMessage('');
    loadInvite();
  }, [token]);

  useEffect(() => {
    const storedInviteToken = sessionStorage.getItem(AUTHOR_INVITE_TOKEN_STORAGE_KEY);
    const inviteTokenToUse = token || storedInviteToken;
    const queryParams = new URLSearchParams(location.search || '');
    const hasAuthCallbackParams =
      queryParams.has('code') ||
      queryParams.has('state') ||
      queryParams.has('error') ||
      queryParams.has('error_description');

    if (!inviteTokenToUse || !invite || isLoading || isAuthenticated || hasAuthCallbackParams) {
      return;
    }

    if (autoLoginTokenRef.current === inviteTokenToUse) {
      return;
    }

    autoLoginTokenRef.current = inviteTokenToUse;
    login(buildKindeOtpLoginOptions(invite, inviteTokenToUse));
  }, [token, invite, location.search, isLoading, isAuthenticated, login]);

  useEffect(() => {
    if (token) {
      return;
    }

    const queryParams = new URLSearchParams(location.search || '');
    const hasAuthCallbackParams =
      queryParams.has('code') ||
      queryParams.has('state') ||
      queryParams.has('error') ||
      queryParams.has('error_description');
    const storedInviteToken = sessionStorage.getItem(AUTHOR_INVITE_TOKEN_STORAGE_KEY);

    if (hasAuthCallbackParams) {
      if (!isLoading && isAuthenticated && storedInviteToken) {
        history.replace(`/lor-author/invite/${storedInviteToken}`);
      }
      return;
    }

    if (storedInviteToken) {
      history.replace(`/lor-author/invite/${storedInviteToken}`);
      return;
    }

    if (!isLoading) {
      setErrorMessage('Missing invite token.');
    }
  }, [token, location.search, history, isLoading, isAuthenticated]);

  useEffect(() => {
    if (!invite || !isAuthenticated) {
      return;
    }
    exchangeSession();
  }, [invite, isAuthenticated]);

  useEffect(() => {
    if (sessionToken && invite) {
      loadAuthorRequest();
    }
  }, [sessionToken, invite, selectedTemplateId]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const uploadSelectedFile = async (fileToUpload) => {
    if (!fileToUpload || !invite || !sessionToken) {
      toast.error('Please select a PDF file first');
      return;
    }

    const headers = await getAuthHeaders();
    headers['x-lor-session'] = sessionToken;
    headers['Content-Type'] = 'multipart/form-data';

    const formData = new FormData();
    formData.append('File', fileToUpload);
    formData.append('sessionToken', sessionToken);
    formData.append('SignatureAttested', signatureAttested ? '1' : '0');

    setIsUploadingDocument(true);
    return axios
      .post(constructUrl(`lor-author/upload?pk_LoRRequest=${invite.pk_LoRRequest}`), formData, {
        headers,
      })
      .then(async () => {
        toast.success('PDF uploaded');
        await loadAuthorRequest(false);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to upload PDF');
      })
      .finally(() => {
        setIsUploadingDocument(false);
      });
  };

  const handleFileSelected = (fileToUpload) => {
    if (!fileToUpload || isLocked) {
      return;
    }

    const fileName = `${fileToUpload.name || ''}`.toLowerCase();
    const isPdfFile =
      `${fileToUpload.type || ''}`.toLowerCase() === 'application/pdf' || fileName.endsWith('.pdf');
    if (!isPdfFile) {
      toast.error('Only PDF uploads are allowed');
      return;
    }

    setSelectedFile(fileToUpload);
    const nextLocalPreviewUrl = URL.createObjectURL(fileToUpload);
    setLocalPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return nextLocalPreviewUrl;
    });
    uploadSelectedFile(fileToUpload);
  };

  const openFilePicker = () => {
    if (isLocked || isUploadingDocument) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const saveFormResponses = async () => {
    if (!invite || !sessionToken) {
      return;
    }

    if (!selectedTemplateId) {
      toast.error('Select a required template first');
      return;
    }

    const headers = await getAuthHeaders();
    headers['x-lor-session'] = sessionToken;

    const answersPayload = questions.map((question) => {
      const currentAnswer = answerMap[question.pk_LoRTemplateQuestion];
      const payload = {
        fk_LoRTemplateQuestion: question.pk_LoRTemplateQuestion,
      };

      if (!currentAnswer) {
        return payload;
      }

      if (currentAnswer.type === 'number') {
        payload.ValueNumber = Number(currentAnswer.value);
      } else if (currentAnswer.type === 'boolean') {
        payload.ValueBool = !!currentAnswer.value;
      } else {
        payload.ValueText = `${currentAnswer.value || ''}`;
      }

      return payload;
    });

    setIsSavingFormResponses(true);
    return putData(
      'lor-author/form',
      { pk_LoRRequest: invite.pk_LoRRequest, sessionToken },
      {
        fk_Template: Number(selectedTemplateId),
        answers: answersPayload,
        sessionToken,
      },
      headers,
    )
      .then(async () => {
        toast.success('Form responses saved');
        await loadAuthorRequest(false);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to save form responses');
      })
      .finally(() => {
        setIsSavingFormResponses(false);
      });
  };

  const submitRequest = async () => {
    if (!invite || !sessionToken) {
      return;
    }

    const headers = await getAuthHeaders();
    headers['x-lor-session'] = sessionToken;

    setIsSubmittingRequest(true);
    return postDataAgnostic(
      'lor-author/submit',
      { pk_LoRRequest: invite.pk_LoRRequest, sessionToken },
      {
        SignatureAttested: signatureAttested,
        sessionToken,
      },
      headers,
    )
      .then(async () => {
        toast.success('Letter submitted and locked');
        await loadAuthorRequest(false);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to submit letter');
      })
      .finally(() => {
        setIsSubmittingRequest(false);
      });
  };

  const renderQuestionInput = (question) => {
    const questionId = question.pk_LoRTemplateQuestion;
    const answerEntry = answerMap[questionId] || { value: '' };
    const responseType = `${question.ResponseType || 'text'}`.toLowerCase();
    const options = parseOptions(question.OptionsJson);

    if (responseType === 'boolean') {
      return (
        <Input
          type="select"
          disabled={isLocked}
          value={`${answerEntry.value === true ? '1' : answerEntry.value === false ? '0' : ''}`}
          onChange={(event) => {
            const value = event.target.value;
            setAnswerMap((prev) => ({
              ...prev,
              [questionId]: {
                value: value === '1' ? true : value === '0' ? false : null,
                type: 'boolean',
              },
            }));
          }}
        >
          <option value="">-- Select --</option>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </Input>
      );
    }

    if ((responseType === 'single_choice' || responseType === 'scale') && options.length > 0) {
      return (
        <Input
          type="select"
          disabled={isLocked}
          value={answerEntry.value || ''}
          onChange={(event) => {
            setAnswerMap((prev) => ({
              ...prev,
              [questionId]: { value: event.target.value, type: 'text' },
            }));
          }}
        >
          <option value="">-- Select --</option>
          {options.map((option, idx) => {
            const optionValue = typeof option === 'string' ? option : option.value || option.label || `${idx}`;
            const optionLabel = typeof option === 'string' ? option : option.label || option.value || `${idx}`;
            return (
              <option key={`${questionId}_${idx}`} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </Input>
      );
    }

    if (responseType === 'scale' && options.length === 0) {
      return (
        <Input
          type="number"
          disabled={isLocked}
          value={answerEntry.value || ''}
          min={question.ScaleMin || 0}
          max={question.ScaleMax || 100}
          onChange={(event) => {
            setAnswerMap((prev) => ({
              ...prev,
              [questionId]: { value: event.target.value, type: 'number' },
            }));
          }}
        />
      );
    }

    if (responseType === 'text') {
      return (
        <Input
          type="textarea"
          disabled={isLocked}
          value={answerEntry.value || ''}
          onChange={(event) => {
            setAnswerMap((prev) => ({
              ...prev,
              [questionId]: { value: event.target.value, type: 'text' },
            }));
          }}
        />
      );
    }

    return (
      <Input
        type="text"
        disabled={isLocked}
        value={answerEntry.value || ''}
        onChange={(event) => {
          setAnswerMap((prev) => ({
            ...prev,
            [questionId]: { value: event.target.value, type: 'text' },
          }));
        }}
      />
    );
  };

  if (isLoading || isLoadingData) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 60 }}>
        <Card style={{ width: '95%', maxWidth: 1200 }}>
          <CardBody>Loading author portal...</CardBody>
        </Card>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Card style={{ width: '95%', maxWidth: 900 }}>
          <CardBody>
            <Alert color="danger" style={{ marginBottom: 0 }}>
              {errorMessage}
            </Alert>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Card style={{ width: '95%', maxWidth: 900 }}>
          <CardBody>Invite not found.</CardBody>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated || !sessionToken) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Card style={{ width: '95%', maxWidth: 900 }}>
          <CardHeader>
            <b>RezRATE Letter Author Portal</b>
          </CardHeader>
          <CardBody>
            <p style={{ marginBottom: 0 }}>
              Opening secure email verification for <b>{invite.AuthorEmail}</b>...
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 20, marginBottom: 30 }}>
      <div style={{ width: '95%', maxWidth: 1200 }}>
        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <b>Letter Request</b>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge color="dark">{`Author: ${invite.AuthorName || invite.AuthorEmail}`}</Badge>
              <Badge color="secondary">{`Letter Type: ${requestDetails?.request?.LetterType ||
                invite.LetterType}`}</Badge>
              <Badge color={isLocked ? 'success' : 'warning'}>{isLocked ? 'Submitted / Locked' : 'Editable'}</Badge>
              {needsSupplement ? <Badge color="warning">Supplement Required</Badge> : null}
            </div>
            {needsSupplement ? (
              <Alert color="warning" style={{ marginTop: 10, marginBottom: 0 }}>
                Program requirements changed after prior submission. Complete missing items and re-submit to finalize.
              </Alert>
            ) : null}
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              Invite expires: {invite.ExpiresAt ? dayjs(invite.ExpiresAt).format('MMM D, YYYY h:mm A') : '--'}
            </div>
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <b>Required Components</b>
          </CardHeader>
          <CardBody>
            {requirementChecklist?.items?.length ? (
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requirementChecklist.items.map((item, index) => {
                    const requirementType = `${item.RequirementType || ''}`.toLowerCase();
                    const label =
                      requirementType === 'template'
                        ? `Standardized form: ${item.TemplateName || `Template #${item.fk_LoRTemplate}`}`
                        : 'PDF letter upload';
                    const status = `${item.Status || ''}`.toLowerCase();

                    return (
                      <tr key={`${label}_${index}`}>
                        <td>{label}</td>
                        <td>
                          <Badge color={status === 'completed' ? 'success' : 'warning'}>{status || 'required'}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <Alert color="light" style={{ marginBottom: 0 }}>
                No explicit requirements found. Default requirement is a PDF letter.
              </Alert>
            )}
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <b>1) Upload PDF Letter</b>
          </CardHeader>
          <CardBody>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={(event) => {
                const nextFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
                handleFileSelected(nextFile);
              }}
            />

            <div
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!isLocked && !isUploadingDocument) {
                  setIsDraggingOverUploadZone(true);
                }
              }}
              onDragLeave={() => {
                setIsDraggingOverUploadZone(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingOverUploadZone(false);
                const droppedFile = event.dataTransfer?.files?.[0] || null;
                handleFileSelected(droppedFile);
              }}
              style={{
                border: `2px dashed ${isDraggingOverUploadZone ? '#2f80ed' : '#c8c8c8'}`,
                borderRadius: 8,
                padding: '28px 16px',
                textAlign: 'center',
                backgroundColor: isDraggingOverUploadZone ? '#f4f8ff' : '#fafafa',
                cursor: isLocked || isUploadingDocument ? 'not-allowed' : 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Drop PDF here or click to browse</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Upload starts automatically after a file is selected.
              </div>
              {selectedFile ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#444' }}>{`Selected: ${selectedFile.name}`}</div>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <Button size="sm" color="secondary" outline disabled={isLocked || isUploadingDocument} onClick={openFilePicker}>
                {submission || selectedFile ? 'Replace File' : 'Upload File'}
              </Button>
              {isUploadingDocument ? (
                <Badge color="info" style={{ alignSelf: 'center' }}>
                  Uploading...
                </Badge>
              ) : null}
            </div>

            {submission ? (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                Uploaded at{' '}
                {dayjs(submission.modifiedAt || submission.createdAt || submission.SubmittedAt).format(
                  'MMM D, YYYY h:mm A',
                )}
              </div>
            ) : null}

            {(uploadedDocumentUrl || localPreviewUrl) ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>PDF Preview</div>
                <iframe
                  title="Uploaded letter preview"
                  src={uploadedDocumentUrl || localPreviewUrl}
                  style={{ width: '100%', minHeight: 420, border: '1px solid #ddd', borderRadius: 6 }}
                />
                <div style={{ marginTop: 8 }}>
                  <a href={uploadedDocumentUrl || localPreviewUrl} target="_blank" rel="noopener noreferrer">
                    Open PDF in new tab
                  </a>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <b>2) Standardized Form</b>
          </CardHeader>
          <CardBody>
            {templateRequirements.length === 0 ? (
              <Alert color="light" style={{ marginBottom: 0 }}>
                No standardized template is required for this request.
              </Alert>
            ) : (
              <>
                {templateRequirements.length > 1 ? (
                  <FormGroup>
                    <Label>Required template</Label>
                    <Input
                      type="select"
                      disabled={isLocked}
                      value={selectedTemplateId || ''}
                      onChange={(event) => {
                        const nextTemplate = event.target.value ? Number(event.target.value) : null;
                        setSelectedTemplateId(nextTemplate);
                      }}
                    >
                      <option value="">Select template</option>
                      {templateRequirements.map((templateRequirement) => (
                        <option key={templateRequirement.fk_LoRTemplate} value={templateRequirement.fk_LoRTemplate}>
                          {templateRequirement.TemplateName || `Template #${templateRequirement.fk_LoRTemplate}`}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                ) : null}

                <Table bordered size="sm" responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '55%' }}>Question</th>
                      <th>Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((question) => {
                      return (
                        <tr key={question.pk_LoRTemplateQuestion}>
                          <td>
                            <b>{question.QuestionKey}</b>
                            <div>{question.Prompt}</div>
                          </td>
                          <td>{renderQuestionInput(question)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <Button color="primary" disabled={isLocked || isSavingFormResponses} onClick={saveFormResponses}>
                  Save Form Responses
                  {isSavingFormResponses ? <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: 6 }} /> : null}
                </Button>
              </>
            )}
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 12 }}>
          <CardHeader>
            <b>3) Finalize Submission</b>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: 10, fontSize: 13, color: '#555' }}>
              Share scope is fixed to all active applications for this applicant.
            </div>
            <FormGroup check style={{ marginBottom: 12 }}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={signatureAttested}
                  disabled={isLocked}
                  onChange={(event) => setSignatureAttested(event.target.checked)}
                />{' '}
                I attest that this submission is complete and authored by me.
              </Label>
            </FormGroup>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button color="success" disabled={isLocked || isSubmittingRequest} onClick={submitRequest}>
                Submit and Lock
                {isSubmittingRequest ? <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: 6 }} /> : null}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <b>Status Timeline</b>
          </CardHeader>
          <CardBody>
            {statusEvents.length === 0 ? (
              <div>No status events yet.</div>
            ) : (
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Actor</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {statusEvents.map((event, idx) => (
                    <tr key={`${event.Status}_${idx}`}>
                      <td>
                        <Badge color={statusColorMap[event.Status] || 'secondary'}>{event.Status}</Badge>
                      </td>
                      <td>{event.ActorType || '--'}</td>
                      <td>{event.CreatedAt ? dayjs(event.CreatedAt).format('MMM D, YYYY h:mm A') : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LoRAuthorPortal;
